import React, { useState, useEffect } from 'react';
import Layout from './components/Layout';
import AuthForm from './components/AuthForm';
import Dashboard from './components/Dashboard';
import VehicleResult from './components/VehicleResult';
import ConsultaPlacaBasicaResultPage from './components/ConsultaPlacaBasicaResult';
import RelatorioProtocoloResult from './components/RelatorioProtocoloResult';
import ConsultaRenainfResult from './components/ConsultaRenainfResult';
import ConsultaPrecoFipeResult from './components/ConsultaPrecoFipeResult';
import ConsultaProprietarioResult from './components/ConsultaProprietarioResult';
import ConsultaLeilaoPrimeResult from './components/ConsultaLeilaoPrimeResult';
import ConsultaSinistroPTResult from './components/ConsultaSinistroPTResult';
import BuyCreditsPage from './components/BuyCreditsPage';
import { User, VehicleData, ConsultaPlacaBasicaResult, SolicitarRelatorioResult, ConsultarRenainfResult, ConsultarPrecoFipeResult, ConsultarProprietarioResult, ConsultarLeilaoPrimeResult, ConsultarSinistroPTResult } from './types';
import { onAuthChange, logout, refreshUserData } from './services/authService';
import { Search, Shield, Zap, Database, CheckCircle2, Loader2 } from 'lucide-react';

type PageType = 'home' | 'login' | 'dashboard' | 'result' | 'result-consulta-basica' | 'result-relatorio' | 'result-renainf' | 'result-fipe' | 'result-proprietario' | 'result-leilao' | 'result-sinistro-pt' | 'buy-credits' | 'payment-success' | 'payment-cancel';

function App() {
  const [user, setUser] = useState<User | null>(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState<PageType>('home');
  const [currentResult, setCurrentResult] = useState<VehicleData | null>(null);
  const [consultaBasicaResult, setConsultaBasicaResult] = useState<ConsultaPlacaBasicaResult | null>(null);
  const [relatorioResult, setRelatorioResult] = useState<SolicitarRelatorioResult | null>(null);
  const [renainfResult, setRenainfResult] = useState<ConsultarRenainfResult | null>(null);
  const [fipeResult, setFipeResult] = useState<ConsultarPrecoFipeResult | null>(null);
  const [proprietarioResult, setProprietarioResult] = useState<ConsultarProprietarioResult | null>(null);
  const [leilaoResult, setLeilaoResult] = useState<ConsultarLeilaoPrimeResult | null>(null);
  const [sinistroPTResult, setSinistroPTResult] = useState<ConsultarSinistroPTResult | null>(null);

  // Listen to auth state changes
  useEffect(() => {
    const unsubscribe = onAuthChange((user) => {
      setUser(user);
      setAuthLoading(false);
      
      // Redirect based on auth state
      if (user && currentPage === 'login') {
        setCurrentPage('dashboard');
      }
    });

    return () => unsubscribe();
  }, [currentPage]);

  // Handle URL for payment redirects
  useEffect(() => {
    const path = window.location.pathname;
    const params = new URLSearchParams(window.location.search);

    if (path === '/payment/success' && params.get('session_id')) {
      setCurrentPage('payment-success');
      // Refresh user data to get updated credits
      setTimeout(async () => {
        const updatedUser = await refreshUserData();
        if (updatedUser) setUser(updatedUser);
        setCurrentPage('dashboard');
      }, 2000);
    } else if (path === '/payment/cancel') {
      setCurrentPage('payment-cancel');
      setTimeout(() => setCurrentPage('dashboard'), 2000);
    }
  }, []);

  const handleLogin = (loggedUser: User) => {
    setUser(loggedUser);
    setCurrentPage('dashboard');
  };

  const handleLogout = async () => {
    await logout();
    setUser(null);
    setCurrentPage('home');
    setCurrentResult(null);
  };

  const handleCreditsUpdate = (credits: number) => {
    if (user) {
      setUser({ ...user, credits });
    }
  };

  const handleShowResult = (data: VehicleData) => {
    setCurrentResult(data);
    setCurrentPage('result');
  };

  const handleShowConsultaBasicaResult = (data: ConsultaPlacaBasicaResult) => {
    setConsultaBasicaResult(data);
    setCurrentPage('result-consulta-basica');
  };

  const handleShowRelatorioResult = (data: SolicitarRelatorioResult) => {
    setRelatorioResult(data);
    setCurrentPage('result-relatorio');
  };

  const handleShowRenainfResult = (data: ConsultarRenainfResult) => {
    setRenainfResult(data);
    setCurrentPage('result-renainf');
  };

  const handleShowFipeResult = (data: ConsultarPrecoFipeResult) => {
    setFipeResult(data);
    setCurrentPage('result-fipe');
  };

  const handleShowProprietarioResult = (data: ConsultarProprietarioResult) => {
    setProprietarioResult(data);
    setCurrentPage('result-proprietario');
  };

  const handleShowLeilaoResult = (data: ConsultarLeilaoPrimeResult) => {
    setLeilaoResult(data);
    setCurrentPage('result-leilao');
  };

  const handleShowSinistroPTResult = (data: ConsultarSinistroPTResult) => {
    setSinistroPTResult(data);
    setCurrentPage('result-sinistro-pt');
  };

  // Render content based on current page
  const renderContent = () => {
    // Auth loading state
    if (authLoading) {
      return (
        <div className="flex flex-col items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 text-emerald-500 animate-spin" />
          <p className="mt-4 text-slate-400">Carregando...</p>
        </div>
      );
    }

    // Payment success
    if (currentPage === 'payment-success') {
      return (
        <div className="flex flex-col items-center justify-center min-h-[60vh]">
          <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mb-4">
            <CheckCircle2 className="w-8 h-8 text-emerald-400" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">Pagamento Confirmado!</h2>
          <p className="text-slate-400">Seus créditos foram adicionados à sua conta.</p>
          <Loader2 className="w-5 h-5 text-emerald-500 animate-spin mt-4" />
        </div>
      );
    }

    // Payment cancelled
    if (currentPage === 'payment-cancel') {
      return (
        <div className="flex flex-col items-center justify-center min-h-[60vh]">
          <div className="w-16 h-16 bg-yellow-500/20 rounded-full flex items-center justify-center mb-4">
            <Shield className="w-8 h-8 text-yellow-400" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">Pagamento Cancelado</h2>
          <p className="text-slate-400">Você pode tentar novamente a qualquer momento.</p>
        </div>
      );
    }

    // Login page
    if (currentPage === 'login') {
      return (
        <div className="py-20 px-4">
          <AuthForm onLogin={handleLogin} />
        </div>
      );
    }

    // Dashboard (authenticated)
    if (currentPage === 'dashboard' && user) {
      return (
        <Dashboard 
          user={user} 
          onCreditsUpdate={handleCreditsUpdate}
          onShowResult={handleShowResult}
          onShowConsultaBasicaResult={handleShowConsultaBasicaResult}
          onShowRelatorioResult={handleShowRelatorioResult}
          onShowRenainfResult={handleShowRenainfResult}
          onShowFipeResult={handleShowFipeResult}
          onShowProprietarioResult={handleShowProprietarioResult}
          onShowLeilaoResult={handleShowLeilaoResult}
          onShowSinistroPTResult={handleShowSinistroPTResult}
          onNavigateToBuyCredits={() => setCurrentPage('buy-credits')}
        />
      );
    }

    // Vehicle result
    if (currentPage === 'result' && currentResult) {
      return (
        <VehicleResult 
          data={currentResult} 
          onBack={() => setCurrentPage('dashboard')} 
        />
      );
    }

    // Consulta Placa Básica result
    if (currentPage === 'result-consulta-basica' && consultaBasicaResult) {
      return (
        <ConsultaPlacaBasicaResultPage 
          data={consultaBasicaResult} 
          onBack={() => setCurrentPage('dashboard')} 
        />
      );
    }

    // Relatório Protocol result
    if (currentPage === 'result-relatorio' && relatorioResult) {
      return (
        <RelatorioProtocoloResult 
          data={relatorioResult} 
          onBack={() => setCurrentPage('dashboard')} 
        />
      );
    }

    // RENAINF result
    if (currentPage === 'result-renainf' && renainfResult) {
      return (
        <ConsultaRenainfResult 
          data={renainfResult} 
          onBack={() => setCurrentPage('dashboard')} 
        />
      );
    }

    // FIPE result
    if (currentPage === 'result-fipe' && fipeResult) {
      return (
        <ConsultaPrecoFipeResult 
          data={fipeResult} 
          onBack={() => setCurrentPage('dashboard')} 
        />
      );
    }

    // Proprietário result
    if (currentPage === 'result-proprietario' && proprietarioResult) {
      return (
        <ConsultaProprietarioResult 
          data={proprietarioResult} 
          onBack={() => setCurrentPage('dashboard')} 
        />
      );
    }

    // Leilão Prime result
    if (currentPage === 'result-leilao' && leilaoResult) {
      return (
        <ConsultaLeilaoPrimeResult 
          data={leilaoResult} 
          onBack={() => setCurrentPage('dashboard')} 
        />
      );
    }

    // Sinistro PT result
    if (currentPage === 'result-sinistro-pt' && sinistroPTResult) {
      return (
        <ConsultaSinistroPTResult 
          data={sinistroPTResult} 
          onBack={() => setCurrentPage('dashboard')} 
        />
      );
    }

    // Buy credits page
    if (currentPage === 'buy-credits' && user) {
      return (
        <BuyCreditsPage 
          user={user}
          onBack={() => setCurrentPage('dashboard')} 
        />
      );
    }

    // Home page (public)
    return (
      <div className="space-y-20 pb-20">
        {/* Hero Section */}
        <div className="relative overflow-hidden bg-slate-900 border-b border-slate-800">
          <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1492144534655-ae79c964c9d7?auto=format&fit=crop&q=80')] bg-cover bg-center opacity-10"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/80 to-transparent"></div>
          
          <div className="relative max-w-7xl mx-auto px-4 py-24 md:py-32 flex flex-col items-center text-center">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-sm font-medium mb-6">
               <Zap className="w-4 h-4" /> Consultas em Tempo Real
            </div>
            <h1 className="text-4xl md:text-6xl font-extrabold text-white tracking-tight mb-6">
              Segurança total na compra <br/> do seu próximo carro.
            </h1>
            <p className="text-lg md:text-xl text-slate-400 max-w-2xl mb-10">
              Consulte multas, restrições, leilões e histórico de roubo em segundos usando nossa tecnologia integrada com bases oficiais.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 w-full max-w-md">
              <button 
                onClick={() => {
                   if(user) setCurrentPage('dashboard');
                   else setCurrentPage('login');
                }}
                className="flex-1 bg-emerald-600 hover:bg-emerald-500 text-white font-bold py-4 rounded-xl shadow-lg shadow-emerald-500/25 transition-all text-lg"
              >
                Consultar Placa Agora
              </button>
            </div>
          </div>
        </div>

        {/* Features */}
        <div className="max-w-7xl mx-auto px-4">
           <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="p-6 bg-slate-900/50 rounded-xl border border-slate-800">
                 <div className="w-12 h-12 bg-indigo-500/10 rounded-lg flex items-center justify-center mb-4">
                    <Database className="w-6 h-6 text-indigo-400" />
                 </div>
                 <h3 className="text-xl font-bold text-white mb-2">Múltiplas Fontes</h3>
                 <p className="text-slate-400">Integração com APIs oficiais para garantir a máxima precisão dos dados.</p>
              </div>
              <div className="p-6 bg-slate-900/50 rounded-xl border border-slate-800">
                 <div className="w-12 h-12 bg-emerald-500/10 rounded-lg flex items-center justify-center mb-4">
                    <CheckCircle2 className="w-6 h-6 text-emerald-400" />
                 </div>
                 <h3 className="text-xl font-bold text-white mb-2">Dados Validados</h3>
                 <p className="text-slate-400">Verificamos restrições judiciais, financeiras e histórico de sinistros em segundos.</p>
              </div>
              <div className="p-6 bg-slate-900/50 rounded-xl border border-slate-800">
                 <div className="w-12 h-12 bg-blue-500/10 rounded-lg flex items-center justify-center mb-4">
                    <Shield className="w-6 h-6 text-blue-400" />
                 </div>
                 <h3 className="text-xl font-bold text-white mb-2">Compra Segura</h3>
                 <p className="text-slate-400">Sistema pré-pago via Stripe. Seus créditos nunca expiram e você só paga o que usar.</p>
              </div>
           </div>
        </div>

        {/* Pricing Preview */}
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white mb-4">Sistema de Créditos Flexível</h2>
            <p className="text-slate-400">Compre créditos e use em qualquer consulta. Sem assinaturas ou mensalidades.</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            <div className="p-6 bg-slate-900/50 rounded-xl border border-slate-800 text-center">
              <div className="w-10 h-10 bg-emerald-500/10 rounded-lg flex items-center justify-center mx-auto mb-3">
                <Search className="w-5 h-5 text-emerald-400" />
              </div>
              <h3 className="text-lg font-bold text-white mb-2">Consultas Rápidas</h3>
              <p className="text-3xl font-bold text-emerald-400 mb-2">1-2 <span className="text-sm font-normal text-slate-400">créditos</span></p>
              <p className="text-sm text-slate-400">Dados do veículo, FIPE e infrações RENAINF</p>
            </div>
            <div className="p-6 bg-purple-900/30 rounded-xl border border-purple-500/30 text-center relative">
              <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-purple-500 text-white text-xs font-bold rounded-full">
                Mais Completo
              </span>
              <div className="w-10 h-10 bg-purple-500/10 rounded-lg flex items-center justify-center mx-auto mb-3">
                <Shield className="w-5 h-5 text-purple-400" />
              </div>
              <h3 className="text-lg font-bold text-white mb-2">Relatório Premium</h3>
              <p className="text-3xl font-bold text-purple-400 mb-2">100+ <span className="text-sm font-normal text-slate-400">créditos</span></p>
              <p className="text-sm text-slate-400">PDF completo com histórico, leilões, sinistros e restrições</p>
            </div>
            <div className="p-6 bg-slate-900/50 rounded-xl border border-slate-800 text-center">
              <div className="w-10 h-10 bg-blue-500/10 rounded-lg flex items-center justify-center mx-auto mb-3">
                <Zap className="w-5 h-5 text-blue-400" />
              </div>
              <h3 className="text-lg font-bold text-white mb-2">Créditos Nunca Expiram</h3>
              <p className="text-3xl font-bold text-blue-400 mb-2">R$ 2,90 <span className="text-sm font-normal text-slate-400">/ crédito</span></p>
              <p className="text-sm text-slate-400">Compre pacotes com desconto e economize</p>
            </div>
          </div>

          <div className="text-center mt-8">
            <button 
              onClick={() => setCurrentPage('login')}
              className="px-8 py-3 bg-slate-800 hover:bg-slate-700 text-white font-medium rounded-xl transition-all"
            >
              Criar conta gratuita
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <Layout 
      user={user} 
      onLogout={handleLogout} 
      onNavigate={(page) => setCurrentPage(page as PageType)} 
      currentPage={currentPage}
    >
      <div className="animate-in fade-in duration-500">
        {renderContent()}
      </div>
    </Layout>
  );
}

export default App;
